import { useState, useMemo } from "react";
import { Layout } from "@/components/layout";
import { useListJournals } from "@workspace/api-client-react";
import { computeJournalStats } from "@/lib/journal-stats";
import { useLifetimeUnfoldingCount } from "@/hooks/use-journal-stats";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { format, isToday, isYesterday, isSameMonth } from "date-fns";
import {
  CalendarDays,
  CalendarCheck,
  List,
  ArrowRight,
  Sparkles,
  BookOpen,
  Layers,
  Lock,
  NotebookPen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { JournalCalendar } from "@/components/journal-calendar";
import { InsightsCard } from "@/components/insights/insights-card";
import { coerceJournalTagForDisplay, type JournalLibraryTag } from "@/lib/journal-tags";
import { cleanSocialSourceTitle } from "@/lib/decode-html-entities";

const TAG_COLORS: Record<JournalLibraryTag, string> = {
  Health: "card-sage",
  Finance: "card-golden",
  "Self-care": "card-blush",
  "Self-growth": "card-lavender",
  Tech: "card-sky",
  Other: "card-terra",
};

const TAG_DOT: Record<JournalLibraryTag, string> = {
  Health: "bg-emerald-300",
  Finance: "bg-amber-300",
  "Self-care": "bg-rose-300",
  "Self-growth": "bg-violet-300",
  Tech: "bg-sky-400",
  Other: "bg-stone-400",
};

/** Takes a full ISO timestamp — a date-only string would be read as UTC midnight. */
function formatDayLabel(iso: string) {
  const d = new Date(iso);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEEE, MMMM d");
}

/** Strip the light markdown the model emits so previews read as plain prose. */
function plain(text: string | null | undefined): string {
  return (text ?? "").replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").trim();
}

interface Journal {
  id: string;
  title: string;
  tag: string;
  createdAt: string;
  sourceTitle?: string | null;
  coreInsight?: string | null;
}

export default function JournalsScreen() {
  const [, setLocation] = useLocation();
  const { data: journals, isLoading } = useListJournals();
  const [view, setView] = useState<"list" | "calendar">("calendar");

  // Mirrors the calendar's own browsed-month / selected-day state, so the stats
  // strip and Weekly Insight below it react to whatever the user is looking at
  // instead of always showing the real, literal "today".
  const [visibleMonth, setVisibleMonth] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  // The calendar reports its own defaults on mount; once it has, we can hand them
  // back so switching to Entries and returning doesn't reset the browsed month.
  const [calendarReported, setCalendarReported] = useState(false);

  const grouped: { label: string; date: string; items: Journal[] }[] = [];
  if (journals) {
    const map = new Map<string, Journal[]>();
    for (const j of journals) {
      const day = format(new Date(j.createdAt), "yyyy-MM-dd");
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(j as Journal);
    }
    for (const [day, items] of map.entries()) {
      grouped.push({ label: formatDayLabel(items[0].createdAt), date: day, items });
    }
  }

  const hasJournals = !!journals && journals.length > 0;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl px-5 py-8">
        <header className="mb-6">
          <h1 className="page-title font-display text-3xl">Your Unfoldings</h1>
          {/* The full line needs ~458px at 14px; a phone column is ~335px, so the
              short form carries the same two ideas rather than wrapping. */}
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="sm:hidden">Revisit reflections, notice the patterns.</span>
            <span className="hidden sm:inline">
              Revisit your reflections and notice the patterns taking shape over time.
            </span>
          </p>
        </header>

        {hasJournals && (
          <SummaryStrip journals={journals as Journal[]} referenceDate={view === "calendar" ? visibleMonth : new Date()} />
        )}

        {/* View toggle — right-aligned to the content container's edge from `sm` up.
            Left-aligned on small phones, where pushing it right would break the
            reading edge the header and stats establish. */}
        {hasJournals && (
          <div className="mb-6 flex justify-start sm:justify-end">
            <div className="inline-flex items-center gap-1 rounded-full border border-foreground/12 bg-white p-[4.5px]">
              <button
                onClick={() => setView("calendar")}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition-all",
                  view === "calendar"
                    ? "bg-white text-foreground shadow-[0_1px_1px_rgb(47_41_37/0.1),0_1px_1.5px_rgb(47_41_37/0.1)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" /> Calendar
              </button>
              <button
                onClick={() => setView("list")}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition-all",
                  view === "list"
                    ? "bg-white text-foreground shadow-[0_1px_1px_rgb(47_41_37/0.1),0_1px_1.5px_rgb(47_41_37/0.1)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <List className="h-3.5 w-3.5" /> Entries
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-6">
            {[1, 2].map((i) => (
              <div key={i}>
                <div className="mb-3 h-4 w-28 animate-pulse rounded bg-muted" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 h-36 animate-pulse rounded-3xl bg-muted" />
                  <div className="h-24 animate-pulse rounded-3xl bg-muted" />
                  <div className="h-24 animate-pulse rounded-3xl bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : !hasJournals ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-4 py-20 text-center"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-lavender/25">
              <NotebookPen className="h-6 w-6 text-lavender-foreground" />
            </div>
            <div>
              <h3 className="font-display text-xl font-medium text-foreground">
                Nothing here yet — and that's fine.
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Your first unfolding will appear here whenever you're ready.
              </p>
            </div>
            <button
              onClick={() => setLocation("/library")}
              className="mt-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary/90"
            >
              Choose a video to unfold
            </button>
            <button
              onClick={() => setLocation("/app")}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Or add a link / thought instead
            </button>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            {view === "list" ? (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
              >
                {grouped.map((group, gIdx) => (
                  <motion.div
                    key={group.date}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: gIdx * 0.06 }}
                  >
                    <div className="mb-3 flex items-center gap-3 px-1">
                      <span className="eyebrow">
                        {group.label}
                      </span>
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground/60">
                        {group.items.length} {group.items.length === 1 ? "unfolding" : "unfoldings"}
                      </span>
                    </div>

                    {group.items.length === 1 ? (
                      <JournalCard
                        journal={group.items[0]}
                        size="large"
                        onClick={() => setLocation(`/journal/${group.items[0].id}`)}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <JournalCard
                            journal={group.items[0]}
                            size="large"
                            onClick={() => setLocation(`/journal/${group.items[0].id}`)}
                          />
                        </div>
                        {group.items.slice(1).map((journal) => (
                          <JournalCard
                            key={journal.id}
                            journal={journal}
                            size="small"
                            onClick={() => setLocation(`/journal/${journal.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="calendar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="surface-card overflow-hidden">
                  <JournalCalendar
                    journals={journals}
                    restoreState={
                      calendarReported ? { month: visibleMonth, selectedDate: selectedDay } : null
                    }
                    onVisibleMonthChange={(m) => {
                      setVisibleMonth(m);
                      setCalendarReported(true);
                    }}
                    onSelectedDateChange={(d) => {
                      setSelectedDay(d);
                      setCalendarReported(true);
                    }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Insights — deliberately outside the view switch: these describe the
            reflections themselves, not the calendar, so they stay put whether
            the user is on Calendar or Entries. */}
        {hasJournals && (
          <section className="mt-8">
            {/* The calendar is the time context; the card's own selector is the level
                of analysis. Falls back to the browsed month when no day is selected. */}
            <InsightsCard
              journals={journals as Journal[]}
              referenceDate={selectedDay ?? visibleMonth}
            />
          </section>
        )}

        {hasJournals && (
          <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
            <Lock className="h-3 w-3" />
            Your unfoldings are private and secure.
          </p>
        )}
      </div>
    </Layout>
  );
}

// ─── Summary strip ───────────────────────────────────────────────────────────

/**
 * Four numbers that make the page feel alive before any card is opened. Each
 * one is derived from the entry list — nothing here is estimated. The first
 * three follow the browsed month; the all-time total deliberately does not,
 * so the strip reads as "what's happening now" beside "everything so far".
 */
function SummaryStrip({ journals, referenceDate }: { journals: Journal[]; referenceDate: Date }) {
  // Authoritative lifetime total from the backend; falls back to the loaded list
  // if /journals/stats is unavailable, so an outage costs one number, not the page.
  const lifetimeCount = useLifetimeUnfoldingCount();
  const stats = useMemo(() => {
    const browsingRealMonth = isSameMonth(referenceDate, new Date());
    const { monthCount, reflectionDays, topTheme, allTimeCount } = computeJournalStats(
      journals,
      referenceDate,
    );
    return {
      monthCount,
      reflectionDays,
      topTheme,
      allTimeCount,
      monthLabel: browsingRealMonth ? "this month" : format(referenceDate, "MMMM"),
    };
  }, [journals, referenceDate]);

  // 2×2 below `lg` so four readouts never crowd a phone/tablet column; one row above it.
  return (
    <div className="mb-7 grid grid-cols-2 gap-x-3 gap-y-5 sm:gap-x-6 sm:gap-y-6 lg:grid-cols-4">
      <StatCard
        icon={<BookOpen className="h-4 w-4 text-lavender-foreground" />}
        tint="bg-lavender/25"
        value={String(stats.monthCount)}
        label="Unfoldings"
        sublabel={stats.monthLabel}
      />
      <StatCard
        icon={<CalendarCheck className="h-4 w-4 text-sage-foreground" />}
        tint="bg-sage/25"
        value={String(stats.reflectionDays)}
        label="Reflection days"
        sublabel={stats.monthLabel}
      />
      <StatCard
        icon={<Sparkles className="h-4 w-4 text-golden-foreground" />}
        tint="bg-golden/25"
        value={stats.topTheme ?? "—"}
        label="Top theme"
        sublabel={stats.monthLabel}
        compact={!!stats.topTheme}
      />
      <StatCard
        icon={<Layers className="h-4 w-4 text-sky-foreground" />}
        tint="bg-sky/25"
        value={String(lifetimeCount ?? stats.allTimeCount)}
        label="Total unfoldings"
        sublabel="all time"
      />
    </div>
  );
}

function StatCard({
  icon,
  tint,
  value,
  label,
  sublabel,
  compact,
}: {
  icon: React.ReactNode;
  tint: string;
  value: string;
  label: string;
  sublabel?: string;
  compact?: boolean;
}) {
  // Three across at every width — stacked under the icon on phones, beside it
  // once there is room. No surface: these are readouts, not controls.
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3.5">
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9",
          tint,
        )}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p
          className={cn(
            "font-display font-medium leading-tight text-foreground",
            compact ? "text-sm sm:text-lg sm:leading-7" : "text-xl sm:text-2xl sm:leading-8",
          )}
        >
          {value}
        </p>
        <p className="text-[11px] leading-tight text-muted-foreground sm:text-xs">
          {label}
          {sublabel && <span className="block">{sublabel}</span>}
        </p>
      </div>
    </div>
  );
}

// ─── Entry card ──────────────────────────────────────────────────────────────

function JournalCard({
  journal,
  size,
  onClick,
}: {
  journal: Journal;
  size: "large" | "small";
  onClick: () => void;
}) {
  const tagKey = coerceJournalTagForDisplay(journal.tag);
  const color = TAG_COLORS[tagKey];
  const dotColor = TAG_DOT[tagKey];
  const snippet = journal.coreInsight ? plain(journal.coreInsight).slice(0, 90) + "…" : null;

  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative w-full overflow-hidden rounded-3xl border border-white/60 text-left transition-all hover:-translate-y-1 hover:shadow-lg active:scale-[0.98]",
        color,
        size === "large" ? "p-5" : "p-4",
      )}
    >
      <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 -translate-y-1/2 translate-x-1/4 rounded-full bg-white/20 blur-2xl" />

      <div className="relative z-10 flex h-full flex-col">
        <div className="mb-2 flex items-center gap-2">
          <div className={cn("h-2 w-2 shrink-0 rounded-full", dotColor)} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-foreground/55">
            {tagKey}
          </span>
          <span className="ml-auto text-[10px] text-foreground/40">
            {format(new Date(journal.createdAt), "h:mm a")}
          </span>
        </div>

        <h3
          className={cn(
            "font-display font-medium leading-snug text-foreground",
            size === "large" ? "mb-2 text-lg" : "mb-1.5 line-clamp-2 text-sm",
          )}
        >
          {journal.title}
        </h3>

        {snippet && (
          <p
            className={cn(
              "leading-relaxed text-foreground/60",
              size === "large" ? "mb-3 line-clamp-2 text-sm" : "mt-0.5 line-clamp-2 text-xs",
            )}
          >
            {snippet}
          </p>
        )}

        {journal.sourceTitle && (
          <p
            className={cn(
              "truncate text-foreground/40",
              size === "large" ? "mt-auto text-xs" : "mt-auto pt-1 text-[10px]",
            )}
          >
            from: {cleanSocialSourceTitle(journal.sourceTitle)}
          </p>
        )}

        {size === "large" && (
          <div className="mt-4 flex items-center justify-end">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/60 transition-colors group-hover:bg-white">
              <ArrowRight className="h-4 w-4 text-foreground/60 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
        )}
      </div>
    </button>
  );
}
